// Brand-specific nutrition lookup. Reads public.brand_nutrition
// (migration 0065) ON DEMAND — never fetchAll. The earlier global
// fetchAll pattern was the single biggest egress source: every
// boot pulled every row in the table, multiplied across users,
// scaled linearly with table size. Replaced with lazy single-row
// fetches keyed by (canonical, brand) or barcode, persisted to
// localStorage with per-row TTL.
//
// Architectural notes:
//   • No more global realtime subscription on this table — was a
//     N×N egress amplifier. Local upsert() injects rows directly.
//     Cross-user updates surface on next cache expiry / lookup.
//   • Two indexes: byKey (canonical::brand) for resolver lookups,
//     byBarcode for scan-path lookups. Same backing table; we
//     hydrate both maps when a row is fetched.
//   • Negative caching — when a fetch returns no row, we cache
//     `null` with a TTL so we don't keep re-asking. Distinguishes
//     "not yet fetched" (undefined) from "we know there's no row"
//     (null cache entry).
//
// API:
//   const { get, getByBarcode, ensureByBarcode, upsert, loading } = useBrandNutrition();
//
//   get(canonical, brand)         — sync read; returns row, null (negative
//                                    cache hit), or undefined (cache miss
//                                    + lazy fetch kicked off in background).
//                                    The caller usually treats null/undefined
//                                    the same way (no brand-specific row).
//   getByBarcode(barcode)         — sync read by barcode. Same semantics.
//   ensureByBarcode(barcode)      — async; returns the row or null after
//                                    fetching. Use in the scan flow when you
//                                    can `await` the result.
//   upsert(...)                   — writes to DB + injects into cache.
//   loading                       — always false now (no global fetch); kept
//                                    for back-compat with consumers that
//                                    branch on it.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "./supabase";
import { validateNutrition } from "./nutrition";

const BrandNutritionContext = createContext({
  get:             () => null,
  getByBarcode:    () => null,
  ensureByBarcode: async () => null,
  upsert:          async () => null,
  loading:         false,
});

function fromDb(row) {
  if (!row) return null;
  return {
    canonicalId:     row.canonical_id,
    brand:           row.brand,
    displayBrand:    row.display_brand,
    nutrition:       row.nutrition || {},
    ingredientsText: row.ingredients_text || null,
    allergens:       Array.isArray(row.allergens) ? row.allergens : null,
    barcode:         row.barcode || null,
    source:          row.source,
    sourceId:        row.source_id || null,
    confidence:      row.confidence ?? 80,
    createdBy:       row.created_by || null,
    createdAt:       row.created_at,
    updatedAt:       row.updated_at,
  };
}

function keyFor(canonicalId, brand) {
  if (!canonicalId || !brand) return null;
  return `${canonicalId}::${String(brand).trim().toLowerCase()}`;
}

// Per-row TTL. Each cached entry carries its own fetchedAt so we
// can age individual rows out without rebuilding the whole map.
// 24h is generous — rows change rarely, and the local upsert path
// keeps the user's own writes fresh in cache instantly.
const ROW_TTL_MS  = 24 * 60 * 60 * 1000;
const CACHE_KEY   = "brandNutritionCache.v2";

// Cache file shape:
//   {
//     byKey:     [ ["canonical::brand", { row, fetchedAt }], ... ],
//     byBarcode: [ [barcode,            { row, fetchedAt }], ... ]
//   }
// We persist as arrays-of-tuples since localStorage can't hold Maps.
function loadFromCache() {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const byKey     = new Map(Array.isArray(parsed.byKey)     ? parsed.byKey     : []);
    const byBarcode = new Map(Array.isArray(parsed.byBarcode) ? parsed.byBarcode : []);
    return { byKey, byBarcode };
  } catch {
    return null;
  }
}

function saveToCache(byKey, byBarcode) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      byKey:     Array.from(byKey.entries()),
      byBarcode: Array.from(byBarcode.entries()),
    }));
  } catch {
    // Quota / private mode — non-fatal.
  }
}

function isFresh(entry) {
  if (!entry || typeof entry.fetchedAt !== "number") return false;
  return Date.now() - entry.fetchedAt <= ROW_TTL_MS;
}

export function BrandNutritionProvider({ children }) {
  // Hydrate from cache instantly so every render after first paint
  // can hit the in-memory Maps. The two Maps are kept in sync via
  // every write path (lazy fetch / upsert / ensureByBarcode).
  const cached = loadFromCache();
  const [byKey,     setByKey]     = useState(() => cached?.byKey     || new Map());
  const [byBarcode, setByBarcode] = useState(() => cached?.byBarcode || new Map());

  // Track in-flight fetches so we don't issue duplicate queries when
  // multiple components ask for the same key in the same render tick.
  const inflightKey = useRef(new Map());     // (canonical::brand) → Promise
  const inflightBC  = useRef(new Map());     // barcode → Promise

  // Persist to localStorage on every map change. Throttled would be
  // nicer but the writes are infrequent enough (one per fetched row)
  // that synchronous JSON.stringify on each is fine.
  useEffect(() => { saveToCache(byKey, byBarcode); }, [byKey, byBarcode]);

  // Inject a fetched row into both maps.
  const ingest = useCallback((row) => {
    if (!row) return;
    const k = keyFor(row.canonicalId, row.brand);
    setByKey(prev => {
      const next = new Map(prev);
      if (k) next.set(k, { row, fetchedAt: Date.now() });
      return next;
    });
    if (row.barcode) {
      setByBarcode(prev => {
        const next = new Map(prev);
        next.set(String(row.barcode), { row, fetchedAt: Date.now() });
        return next;
      });
    }
  }, []);

  // Negative cache write — record "we asked, no row exists" so we
  // don't keep re-fetching. Caller passes one of the indexes; we
  // record on whichever is keyed.
  const ingestMiss = useCallback(({ canonicalId, brand, barcode }) => {
    const k = keyFor(canonicalId, brand);
    if (k) {
      setByKey(prev => {
        const next = new Map(prev);
        next.set(k, { row: null, fetchedAt: Date.now() });
        return next;
      });
    }
    if (barcode) {
      setByBarcode(prev => {
        const next = new Map(prev);
        next.set(String(barcode), { row: null, fetchedAt: Date.now() });
        return next;
      });
    }
  }, []);

  // Lazy fetch by (canonical, brand). Dedupes in-flight requests.
  const fetchByKey = useCallback(async (canonicalId, brand) => {
    const k = keyFor(canonicalId, brand);
    if (!k) return null;
    if (inflightKey.current.has(k)) return inflightKey.current.get(k);
    const promise = (async () => {
      const normalized = String(brand).trim().toLowerCase();
      const { data, error } = await supabase
        .from("brand_nutrition")
        .select("*")
        .eq("canonical_id", canonicalId)
        .eq("brand", normalized)
        .maybeSingle();
      if (error && error.code !== "PGRST116") {
        console.warn("[brand_nutrition] lazy fetch failed:", error.message);
        return null;
      }
      const row = data ? fromDb(data) : null;
      if (row) ingest(row);
      else ingestMiss({ canonicalId, brand: normalized });
      return row;
    })()
      .finally(() => { inflightKey.current.delete(k); });
    inflightKey.current.set(k, promise);
    return promise;
  }, [ingest, ingestMiss]);

  // Lazy fetch by barcode.
  const fetchByBarcode = useCallback(async (barcode) => {
    const bc = String(barcode || "").trim();
    if (!bc) return null;
    if (inflightBC.current.has(bc)) return inflightBC.current.get(bc);
    const promise = (async () => {
      const { data, error } = await supabase
        .from("brand_nutrition")
        .select("*")
        .eq("barcode", bc)
        .maybeSingle();
      if (error && error.code !== "PGRST116") {
        console.warn("[brand_nutrition] barcode lazy fetch failed:", error.message);
        return null;
      }
      const row = data ? fromDb(data) : null;
      if (row) ingest(row);
      else ingestMiss({ barcode: bc });
      return row;
    })()
      .finally(() => { inflightBC.current.delete(bc); });
    inflightBC.current.set(bc, promise);
    return promise;
  }, [ingest, ingestMiss]);

  // Sync read by (canonical, brand). Returns:
  //   row    — fresh cache hit
  //   null   — known-empty (negative cache)
  //   undef  — cache miss; lazy fetch kicked off in background, the
  //            consumer will re-render when the fetch resolves and
  //            setByKey re-publishes.
  // Most resolver call sites treat null and undefined the same
  // ("no brand-specific data, fall through to canonical"), so this
  // matches existing behavior cleanly.
  const get = useCallback((canonicalIdOrKey, maybeBrand) => {
    let canonical, brand;
    if (typeof maybeBrand === "string") {
      canonical = canonicalIdOrKey;
      brand     = maybeBrand;
    } else if (typeof canonicalIdOrKey === "string" && canonicalIdOrKey.includes("::")) {
      const [c, b] = canonicalIdOrKey.split("::");
      canonical = c;
      brand     = b;
    } else {
      return null;
    }
    const k = keyFor(canonical, brand);
    if (!k) return null;
    const entry = byKey.get(k);
    if (entry && isFresh(entry)) return entry.row;
    // Stale or missing — kick off background fetch (fire-and-forget).
    fetchByKey(canonical, brand);
    return entry?.row || null;  // serve stale row if any
  }, [byKey, fetchByKey]);

  const getByBarcode = useCallback((barcode) => {
    const bc = String(barcode || "").trim();
    if (!bc) return null;
    const entry = byBarcode.get(bc);
    if (entry && isFresh(entry)) return entry.row;
    fetchByBarcode(bc);
    return entry?.row || null;
  }, [byBarcode, fetchByBarcode]);

  // Async ensure — explicit await for the scan flow. Used by
  // lookupBarcode to check the cache before falling through to OFF.
  const ensureByBarcode = useCallback(async (barcode) => {
    const bc = String(barcode || "").trim();
    if (!bc) return null;
    const entry = byBarcode.get(bc);
    if (entry && isFresh(entry)) return entry.row;
    return fetchByBarcode(bc);
  }, [byBarcode, fetchByBarcode]);

  // Write path — preserved from the original. Persists to DB and
  // injects the fresh row into both indexes so the user's own writes
  // are visible immediately without round-tripping.
  const upsert = useCallback(async ({
    canonicalId, brand, nutrition,
    barcode = null, source = "user", sourceId = null, confidence = 80,
    ingredientsText = null,
    allergens = null,
  }) => {
    if (!canonicalId || !brand || !nutrition) {
      throw new Error("brand_nutrition upsert needs canonicalId + brand + nutrition");
    }
    const check = validateNutrition(nutrition);
    if (!check.ok) {
      console.warn(`[brand_nutrition] refusing malformed nutrition for ${canonicalId}/${brand}: ${check.reason}`);
      return null;
    }
    const normalizedBrand = String(brand).trim().toLowerCase();
    const displayBrand    = String(brand).trim();
    const { data: { user } = {} } = await supabase.auth.getUser();
    const row = {
      canonical_id:  canonicalId,
      brand:         normalizedBrand,
      display_brand: displayBrand,
      nutrition,
      barcode,
      source,
      source_id:     sourceId,
      confidence,
      ...(ingredientsText != null ? { ingredients_text: ingredientsText } : {}),
      ...(allergens != null ? { allergens } : {}),
      ...(user?.id ? { created_by: user.id } : {}),
    };
    const { data, error } = await supabase
      .from("brand_nutrition")
      .upsert(row, { onConflict: "canonical_id,brand" })
      .select()
      .single();
    if (error) {
      const level = error?.code === "42501" ? "warn" : "error";
      console[level]("[brand_nutrition] upsert failed:", error);
      return null;
    }
    const fresh = fromDb(data);
    if (fresh) ingest(fresh);
    return fresh;
  }, [ingest]);

  const value = useMemo(() => ({
    get,
    getByBarcode,
    ensureByBarcode,
    upsert,
    loading: false,
  }), [get, getByBarcode, ensureByBarcode, upsert]);

  return (
    <BrandNutritionContext.Provider value={value}>
      {children}
    </BrandNutritionContext.Provider>
  );
}

export function useBrandNutrition() {
  return useContext(BrandNutritionContext);
}
